import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Group as GroupIcon,
  CheckCircle as AvailableIcon,
  Warning as ConflictIcon,
  CalendarToday as CalendarIcon,
  ViewList as ListIcon
} from '@mui/icons-material';
import { useGroupAvailability } from '../contexts/GroupAvailabilityContext';
import { useGroups } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';

import {
  getCalendarDays,
  getMonthYearLabel,
  getDayLabels,
  isInCurrentMonth,
  isCurrentDay,
  isPastDate,
  getNextMonth,
  getPreviousMonth,
  formatDate,
  isWeekend,
  isWeekday
} from '../utils/calendarUtils';

export default function GroupCalendar({ includeWeekdays = false, selectedGroupId = null, fullHeight = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const { groups, loading: groupsLoading } = useGroups();
  const {
    getGroupAvailability
  } = useGroupAvailability();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupMemberAvailability, setGroupMemberAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  // Set initial group when groups load or when selectedGroupId prop changes
  useEffect(() => {
    if (selectedGroupId && groups.some(g => g.id === selectedGroupId)) {
      setSelectedGroup(selectedGroupId);
    } else if (groups.length > 0 && !selectedGroup) {
      setSelectedGroup(groups[0].id);
    }
  }, [groups, selectedGroup, selectedGroupId]);

  // Load group member availability when group or month changes
  useEffect(() => {
    if (selectedGroup && getGroupAvailability) {
      const loadAvailability = async () => {
        setLoadingAvailability(true);

        try {
          console.log('GroupCalendar: Loading availability for group:', selectedGroup, 'month:', currentMonth, 'includeWeekdays:', includeWeekdays);
          const availability = await getGroupAvailability(selectedGroup, includeWeekdays, currentMonth);
          console.log('GroupCalendar: Received availability data:', availability);
          console.log('GroupCalendar: Number of dates with data:', Object.keys(availability).length);
          setGroupMemberAvailability(availability);
        } catch (error) {
          console.error('Error loading group availability:', error);
        } finally {
          setLoadingAvailability(false);
        }
      };

      loadAvailability();
    }
  }, [selectedGroup, currentMonth, getGroupAvailability, includeWeekdays]);

  // Handle group change
  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
  };

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    return getCalendarDays(currentMonth);
  }, [currentMonth]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(getPreviousMonth(currentMonth));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(getNextMonth(currentMonth));
  };

  // Get availability stats for the selected group
  const getAvailabilityStats = () => {
    if (!selectedGroup || !groupMemberAvailability) return {};

    const availability = groupMemberAvailability;
    const dates = Object.values(availability);

    console.log('GroupCalendar: Calculating stats for group:', selectedGroup);
    console.log('GroupCalendar: Total availability data:', dates.length);
    console.log('GroupCalendar: Sample dates:', dates.slice(0, 3));

    if (dates.length === 0) return {};

    // Get current date for filtering out past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter dates to only include the currently viewed month
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    console.log('GroupCalendar: Filtering dates for month:', currentMonth);
    console.log('GroupCalendar: Month range:', currentMonthStart.toDateString(), 'to', currentMonthEnd.toDateString());

    // Filter dates based on the current toggle state, exclude past dates, and only include current month
    const filteredDates = dates.filter(dateData => {
      if (!dateData.date) return false;

      // Only include dates from the currently viewed month
      if (dateData.date < currentMonthStart || dateData.date > currentMonthEnd) return false;

      // Exclude past dates (only if they're in the current month)
      if (dateData.date < today && dateData.date >= currentMonthStart) return false;

      if (includeWeekdays) {
        // Include all dates in current month when toggle is ON
        return true;
      } else {
        // Only include weekend dates in current month when toggle is OFF
        return isWeekend(dateData.date);
      }
    });

    console.log('GroupCalendar: Filtered dates count:', filteredDates.length);
    console.log('GroupCalendar: Filtered dates:', filteredDates.map(d => d.date.toDateString()));

    if (filteredDates.length === 0) {
      return {
        totalDates: 0,
        conflictDates: 0,
        averageAvailability: 0,
        totalMembers: 0
      };
    }

    const totalDates = filteredDates.length;
    const conflictDates = filteredDates.filter(d => d.hasConflicts).length;
    const totalMembers = filteredDates[0]?.totalMembers || 0;

    // Calculate average availability percentage
    const totalAvailability = filteredDates.reduce((sum, date) => {
      return sum + (date.availableMembers.length / date.totalMembers);
    }, 0);
    const averageAvailability = totalDates > 0 ? (totalAvailability / totalDates) * 100 : 0;

    const stats = {
      totalDates,
      conflictDates,
      averageAvailability: Math.round(averageAvailability),
      totalMembers
    };

    console.log('GroupCalendar: Final stats:', stats);
    return stats;
  };

  // Render calendar day
  const renderCalendarDay = (date) => {
    const isCurrentMonth = isInCurrentMonth(date, currentMonth);
    const isToday = isCurrentDay(date);
    const isPast = isPastDate(date);
    const isWeekendDay = isWeekend(date);
    const isWeekdayDay = isWeekday(date);

    // Get availability data for this date
    const dateString = date.toDateString();
    const groupAvailability = groupMemberAvailability[dateString];
    const hasConflict = groupAvailability?.hasConflicts || false;
    const availableCount = groupAvailability?.availableMembers?.length || 0;
    const totalMembers = groupAvailability?.totalMembers || 0;

    // Check specific unavailability types
    const isMeUnavailable = groupAvailability?.unavailableMembers?.some(
      member => member.userId === currentUser?.uid
    ) || false;

    const isOthersUnavailable = groupAvailability?.unavailableMembers?.some(
      member => member.userId !== currentUser?.uid
    ) || false;

    // Debug logging for all dates with availability data
    if (groupAvailability) {
      console.log('Calendar: Date rendering:', dateString, {
        isCurrentMonth,
        isToday,
        isPast,
        hasConflict,
        availableCount,
        totalMembers,
        isMeUnavailable,
        isOthersUnavailable,
        unavailableMembers: groupAvailability.unavailableMembers?.length || 0
      });
    }

    // Debug logging for conflicts
    if (groupAvailability && groupAvailability.unavailableMembers && groupAvailability.unavailableMembers.length > 0) {
      console.log('Calendar: Date has conflicts:', dateString, {
        unavailableMembers: groupAvailability.unavailableMembers.length,
        hasConflicts: groupAvailability.hasConflicts,
        hasConflict: hasConflict,
        isMeUnavailable: isMeUnavailable,
        isOthersUnavailable: isOthersUnavailable
      });
    }

    // If weekdays are disabled and this is a weekday, show disabled state
    if (!includeWeekdays && isWeekdayDay) {
      return (
        <Box
          key={date.toISOString()}
          sx={{
            // aspectRatio: '1', // Removed for flexible height
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            position: 'relative',
            backgroundColor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.400',
            borderRadius: 1,
            p: 0.5,
            opacity: isCurrentMonth ? 1 : 0.4
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontSize: isMobile ? '0.75rem' : '0.875rem'
            }}
          >
            {date.getDate()}
          </Typography>

          {/* Weekday indicator when toggle is OFF */}
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'grey.500'
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        key={date.toISOString()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isPast ? 'default' : 'pointer',
          position: 'relative',
          backgroundColor: isToday
            ? 'transparent'
            : isPast
              ? 'action.hover' // Match disabled weekdays style
              : hasConflict
                ? 'warning.50'
                : 'transparent',
          border: isToday
            ? '2px solid'
            : '1px solid',
          borderColor: isToday
            ? 'secondary.main'
            : 'divider', // Unified border color for all other states
          borderRadius: 1,
          p: 0.5,
          outline: 'none', // Prevent default focus ring
          overflow: 'hidden', // Clip stripes to border radius
          opacity: isCurrentMonth ? 1 : 0.4,
          '&:hover': !isPast && {
            backgroundColor: hasConflict
              ? 'warning.100'
              : 'action.hover',
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isToday ? 'bold' : 'normal',
            // mb: 1, // Removed to fix centering
            color: isToday
              ? 'secondary.main'
              : isPast
                ? 'text.disabled'
                : hasConflict
                  ? 'warning.dark'
                  : 'text.primary',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}
        >
          {date.getDate()}
        </Typography>

        {/* Group availability indicators */}
        {groupAvailability && (
          <>
            {/* Available members count - Top Center (moved to avoid stripes) */}
            {groupAvailability.availableMembers && groupAvailability.availableMembers.length > 0 &&
              (includeWeekdays || isWeekendDay) && !isPast && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}
                >
                  <AvailableIcon sx={{ fontSize: 10, color: 'success.main' }} />
                  <Typography variant="caption" color="success.main" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                    {groupAvailability.availableMembers.length}
                  </Typography>
                </Box>
              )}

            {/* Me Unavailable (Red) - Left Side Stripe */}
            {isMeUnavailable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  bottom: 4,
                  left: 0,
                  width: 4,
                  borderTopRightRadius: 2,
                  borderBottomRightRadius: 2,
                  backgroundColor: 'error.main'
                }}
              />
            )}

            {/* Others Unavailable (Orange) - Right Side Stripe */}
            {isOthersUnavailable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  bottom: 4,
                  right: 0,
                  width: 4,
                  borderTopLeftRadius: 2,
                  borderBottomLeftRadius: 2,
                  backgroundColor: 'warning.main'
                }}
              />
            )}

            {/* Tooltip for conflict details */}
            {hasConflict && (
              <Tooltip
                title={`${groupAvailability.unavailableMembers.length} member(s) unavailable`}
                arrow
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: 'help'
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      </Box>
    );
  };

  // Render list view
  const renderListView = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get filtered dates for list view
    const filteredDates = Object.entries(groupMemberAvailability)
      .filter(([dateString, availability]) => {
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);

        // Exclude past dates
        if (date < today) return false;

        // Filter by weekday/weekend toggle
        if (includeWeekdays) return true;
        return isWeekend(date);
      })
      .sort(([a], [b]) => new Date(a) - new Date(b));

    if (filteredDates.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No dates match the current filters
          </Typography>
        </Box>
      );
    }

    return (
      <List dense>
        {filteredDates.map(([dateString, availability]) => {
          const date = new Date(dateString);
          const hasConflicts = availability.hasConflicts;


          return (
            <ListItem key={dateString} divider sx={{ px: 0 }}>
              <ListItemAvatar>
                <Avatar sx={{
                  width: 32,
                  height: 32,
                  bgcolor: hasConflicts
                    ? 'warning.main'
                    : 'success.main'
                }}>
                  {hasConflicts ? <ConflictIcon fontSize="small" /> : <AvailableIcon fontSize="small" />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={formatDate(date, 'EEEE, MMMM d, yyyy')}
                secondary={`Available: ${availability.availableMembers.length}/${availability.totalMembers} members`}
              />
              {/* Additional info below the ListItemText to avoid nested paragraphs */}
              {availability.unavailableMembers.length > 0 && (
                <Box sx={{ mt: 1, ml: 7 }}>
                  <Typography variant="caption" color="error" component="span">
                    Unavailable: {availability.unavailableMembers.map(m => m.name).join(', ')}
                  </Typography>
                </Box>
              )}

            </ListItem>
          );
        })}
      </List>
    );
  };

  if (groupsLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading groups...</Typography>
      </Paper>
    );
  }

  if (groups.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          <Typography variant="h6" gutterBottom>
            No Groups Available
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You need to be a member of at least one group to view group availability.
          </Typography>
          <Button variant="contained" color="primary">
            Create or Join a Group
          </Button>
        </Alert>
      </Paper>
    );
  }

  const selectedGroupData = groups.find(g => g.id === selectedGroup);
  const availabilityStats = getAvailabilityStats();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      gap: 3,
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Main Calendar */}
      <Box sx={{
        flex: { xs: '1 1 100%', lg: '0 0 65%' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        <Paper sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header Row: Group Select + View Mode + Month Nav */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
            {/* Group Select */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Select Group"
                onChange={handleGroupChange}
                disabled={groupsLoading}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon fontSize="small" />
                      {group.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* View Mode */}
            <Tabs
              value={viewMode}
              onChange={(e, newValue) => setViewMode(newValue)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
            >
              <Tab value="calendar" icon={<CalendarIcon fontSize="small" />} />
              <Tab value="list" icon={<ListIcon fontSize="small" />} />
            </Tabs>

            {/* Month Nav */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={goToPreviousMonth} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle2" sx={{ minWidth: 100, textAlign: 'center' }}>
                {getMonthYearLabel(currentMonth)}
              </Typography>
              <IconButton onClick={goToNextMonth} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Legend - Compact */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 4, borderRadius: 1, bgcolor: 'warning.main' }} />
              <Typography variant="caption">Conflict</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 4, borderRadius: 1, bgcolor: 'error.main' }} />
              <Typography variant="caption">You</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AvailableIcon sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="caption">Available</Typography>
            </Box>
          </Box>

          {/* Loading indicator */}
          {loadingAvailability && (
            <LinearProgress sx={{ mb: 1 }} />
          )}

          {/* Calendar Grid */}
          {viewMode === 'calendar' && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0
            }}>
              {/* Day Labels */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 0.5 }}>
                {getDayLabels().map((day) => (
                  <Box
                    key={day}
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    {isMobile ? day.charAt(0) : day}
                  </Box>
                ))}
              </Box>

              {/* Calendar Days */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                // gridTemplateRows: 'repeat(5, 1fr)', // Removed to prevent forced stretching
                gap: 1,
                // flex: 1, // Removed to prevent forced stretching
                width: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                aspectRatio: '1.1', // Taller boxes (was 1.4)
                mx: 'auto', // Center horizontally only
                alignContent: 'start'
              }}>
                {calendarDays.map((date) => (
                  <Box key={date.toISOString()} sx={{ height: '100%' }}>
                    {renderCalendarDay(date)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {renderListView()}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Sidebar */}
      <Box sx={{
        flex: { xs: '1 1 100%', lg: '0 0 35%' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflowY: 'auto'
      }}>
        {/* Group Info */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {selectedGroupData?.name || 'Group Info'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedGroupData?.members?.length || 0} members sharing availability
          </Typography>
        </Paper>

        {/* Availability Stats */}
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Stats
          </Typography>

          {Object.keys(availabilityStats).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Available dates progress bar */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Available
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {availabilityStats.totalDates - availabilityStats.conflictDates} / {availabilityStats.totalDates}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={((availabilityStats.totalDates - availabilityStats.conflictDates) / availabilityStats.totalDates) * 100 || 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                  color="success"
                />
              </Box>

              {/* Conflict dates progress bar */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Conflicts
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {availabilityStats.conflictDates} / {availabilityStats.totalDates}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(availabilityStats.conflictDates / availabilityStats.totalDates) * 100 || 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                  color="warning"
                />
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No availability data available
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
